import { useState } from 'react';
import { MarketingCompany, MarketingPage } from '../types';
import { useMarketingData } from '../MarketingContext';

interface Props { onNavigate: (page: MarketingPage) => void; }

export default function CompaniesPage({ onNavigate }: Props) {
  const { companies, projects, products, campaigns, updateCompany } = useMarketingData();
  const [search, setSearch] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<MarketingCompany | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState<Partial<MarketingCompany>>({});
  const [showAddModal, setShowAddModal] = useState(false);

  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.industry.toLowerCase().includes(search.toLowerCase())
  );

  const companyProjects = selectedCompany ? projects.filter(p => p.companyId === selectedCompany.id) : [];
  const companyProducts = selectedCompany ? products.filter(p => p.companyId === selectedCompany.id) : [];
  const companyCampaigns = selectedCompany ? campaigns.filter(c => c.companyId === selectedCompany.id) : [];

  const handleSave = () => {
    if (selectedCompany && editMode) {
      updateCompany(selectedCompany.id, editData);
      setSelectedCompany({ ...selectedCompany, ...editData });
      setEditMode(false);
      setEditData({});
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'projects', label: 'Projects', icon: '📋' },
    { id: 'products', label: 'Products', icon: '📦' },
    { id: 'campaigns', label: 'Campaigns', icon: '📣' },
    { id: 'competitors', label: 'Competitors', icon: '⚔️' },
    { id: 'analytics', label: 'Analytics', icon: '📈' },
    { id: 'documents', label: 'Documents', icon: '📝' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
          <p className="text-sm text-gray-500">Manage all companies, their strategies, projects, and products</p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">+ Add Company</button>
      </div>

      {/* Search */}
      <div className="bg-white border border-gray-200 rounded-xl p-3">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search companies..." className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500" />
      </div>

      {selectedCompany ? (
        /* Company Detail View */
        <div className="space-y-4">
          <button onClick={() => { setSelectedCompany(null); setEditMode(false); }} className="text-sm text-blue-600 hover:underline">← Back to Companies</button>
          
          {/* Company Header */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-xl bg-gray-100 flex items-center justify-center text-2xl">{selectedCompany.logo}</div>
                <div>
                  {editMode ? (
                    <input value={editData.name ?? selectedCompany.name} onChange={e => setEditData({ ...editData, name: e.target.value })} className="text-xl font-bold text-gray-900 border border-gray-300 rounded px-2 py-1" />
                  ) : (
                    <h2 className="text-xl font-bold text-gray-900">{selectedCompany.name}</h2>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">{selectedCompany.industry}</span>
                    <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded-full">{selectedCompany.status}</span>
                    <span className="text-xs text-gray-400">Budget: ₹{(selectedCompany.budget / 100000).toFixed(1)}L</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                {editMode ? (
                  <>
                    <button onClick={handleSave} className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg">Save</button>
                    <button onClick={() => { setEditMode(false); setEditData({}); }} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg">Cancel</button>
                  </>
                ) : (
                  <button onClick={() => { setEditMode(true); setEditData({}); }} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200">✏️ Edit</button>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1.5">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Description</h3>
                    <p className="text-sm text-gray-600">{selectedCompany.description}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Brand Positioning</h3>
                    <p className="text-sm text-gray-600">{selectedCompany.brandPositioning}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Target Market</h3>
                    <p className="text-sm text-gray-600">{selectedCompany.targetMarket}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Target Customers</h3>
                    <p className="text-sm text-gray-600">{selectedCompany.targetCustomers}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Objectives</h3>
                    <p className="text-sm text-gray-600">{selectedCompany.objectives}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Marketing Objectives</h3>
                    <p className="text-sm text-gray-600">{selectedCompany.marketingObjectives}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Challenges</h3>
                    <p className="text-sm text-gray-600">{selectedCompany.challenges}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Opportunities</h3>
                    <p className="text-sm text-gray-600">{selectedCompany.opportunities}</p>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Current Marketing Channels</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedCompany.currentChannels.map(ch => (
                      <span key={ch} className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full">{ch}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Regions</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedCompany.regions.map(r => (
                      <span key={r} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full">{r}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'projects' && (
              <div className="space-y-3">
                {companyProjects.length === 0 ? (
                  <div className="text-center py-8 text-sm text-gray-400">No projects for this company yet.</div>
                ) : companyProjects.map(p => (
                  <div key={p.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer" onClick={() => onNavigate('marketing-projects')}>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{p.name}</div>
                      <div className="text-xs text-gray-500">{p.description.slice(0, 80)}...</div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === 'in_progress' ? 'bg-green-100 text-green-700' : p.status === 'planning' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{p.status}</span>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'products' && (
              <div className="space-y-3">
                {companyProducts.length === 0 ? (
                  <div className="text-center py-8 text-sm text-gray-400">No products for this company yet.</div>
                ) : companyProducts.map(p => (
                  <div key={p.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm font-medium text-gray-900">{p.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{p.description.slice(0, 80)}...</div>
                    <div className="flex gap-1.5 mt-2">
                      <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full">{p.category}</span>
                      <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded-full">{p.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'campaigns' && (
              <div className="space-y-3">
                {companyCampaigns.length === 0 ? (
                  <div className="text-center py-8 text-sm text-gray-400">No campaigns for this company yet.</div>
                ) : companyCampaigns.map(c => (
                  <div key={c.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-900">{c.name}</div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === 'in_progress' ? 'bg-green-100 text-green-700' : c.status === 'planned' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{c.status}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">Budget: ₹{(c.budget / 1000).toFixed(0)}K | {c.channels.join(', ')}</div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'competitors' && (
              <div className="space-y-2">
                {selectedCompany.competitors.map((comp, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="h-8 w-8 rounded-lg bg-red-50 flex items-center justify-center text-sm">⚔️</div>
                    <span className="text-sm font-medium text-gray-800">{comp}</span>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'analytics' && (
              <div className="text-center py-8 text-sm text-gray-400">Analytics data will appear here once campaigns are running.</div>
            )}

            {activeTab === 'documents' && (
              <div className="text-center py-8 text-sm text-gray-400">No documents uploaded yet.</div>
            )}
          </div>
        </div>
      ) : (
        /* Companies List */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(co => {
            const pCount = projects.filter(p => p.companyId === co.id).length;
            const cCount = campaigns.filter(c => c.companyId === co.id).length;
            const pdCount = products.filter(p => p.companyId === co.id).length;
            return (
              <button key={co.id} onClick={() => setSelectedCompany(co)} className="bg-white border border-gray-200 rounded-xl p-5 text-left hover:shadow-md hover:border-blue-200 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-12 w-12 rounded-xl bg-gray-100 flex items-center justify-center text-xl">{co.logo}</div>
                  <div>
                    <div className="text-sm font-bold text-gray-900">{co.name}</div>
                    <div className="text-xs text-gray-500">{co.industry}</div>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mb-3 line-clamp-2">{co.description}</p>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span>📋 {pCount} projects</span>
                  <span>📣 {cCount} campaigns</span>
                  <span>📦 {pdCount} products</span>
                </div>
                <div className="flex items-center gap-1.5 mt-3">
                  <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded-full">{co.status}</span>
                  <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">₹{(co.budget / 100000).toFixed(1)}L budget</span>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {co.currentChannels.slice(0, 3).map(ch => (
                    <span key={ch} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">{ch}</span>
                  ))}
                  {co.currentChannels.length > 3 && <span className="text-[10px] text-gray-400">+{co.currentChannels.length - 3}</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Add Company Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Add New Company</h3>
            <div className="space-y-3">
              <div><label className="text-xs font-medium text-gray-700 block mb-1">Company Name *</label><input id="new-co-name" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" placeholder="e.g., New Company Ltd" /></div>
              <div><label className="text-xs font-medium text-gray-700 block mb-1">Industry *</label><input id="new-co-industry" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" placeholder="e.g., Manufacturing" /></div>
              <div><label className="text-xs font-medium text-gray-700 block mb-1">Description</label><textarea id="new-co-desc" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" rows={2} placeholder="Brief description..." /></div>
              <div><label className="text-xs font-medium text-gray-700 block mb-1">Business Model</label><input id="new-co-model" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" placeholder="e.g., B2B Manufacturing" /></div>
              <div><label className="text-xs font-medium text-gray-700 block mb-1">Marketing Budget (₹)</label><input id="new-co-budget" type="number" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" placeholder="e.g., 500000" /></div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowAddModal(false)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg">Cancel</button>
              <button onClick={() => {
                const name = (document.getElementById('new-co-name') as HTMLInputElement)?.value;
                const industry = (document.getElementById('new-co-industry') as HTMLInputElement)?.value;
                if (name && industry) {
                  const newCo: MarketingCompany = {
                    id: 'co-' + Date.now(), name, logo: '🏢', industry,
                    description: (document.getElementById('new-co-desc') as HTMLTextAreaElement)?.value || '',
                    businessModel: (document.getElementById('new-co-model') as HTMLInputElement)?.value || '',
                    targetMarket: '', regions: [], targetCustomers: '', objectives: '', marketingObjectives: '',
                    brandPositioning: '', competitors: [], currentChannels: [], maturity: '',
                    challenges: '', opportunities: '', budget: Number((document.getElementById('new-co-budget') as HTMLInputElement)?.value) || 0,
                    team: '', status: 'planning', notes: '', projectIds: [], productIds: [], documentIds: [],
                    createdAt: new Date().toISOString().split('T')[0], updatedAt: new Date().toISOString().split('T')[0],
                  };
                  useMarketingData().addCompany(newCo);
                  setShowAddModal(false);
                }
              }} className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">Add Company</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
